import { Container, Graphics } from 'pixi.js';
import { TILE_SIZE } from '../main.js';
import { WALKABLE_TILES } from '../systems/DungeonGenerator.js';
import { hasLineOfSight } from '../systems/Pathfinding.js';

/**
 * Enemy entity with A* pathfinding AI.
 * States: idle (patrol), chase (path-follow), attack, hurt, dead.
 */
export class Enemy {
  constructor(x, y, config) {
    this.x = x;
    this.y = y;
    this.hp = config.hp;
    this.maxHp = config.hp;
    this.damage = config.damage;
    this.speed = config.speed;
    this.size = config.size;
    this.color = typeof config.color === 'string' ? parseInt(config.color, 16) : config.color;
    this.name = config.name || 'Enemy';
    this.xpReward = config.xpReward || 0;

    // Ranged enemy properties (M16)
    this.isRanged = config.isRanged || false;
    this.projectileSpeed = config.projectileSpeed || 120;
    this.rangedAttackRange = config.attackRange || 96;
    this.rangedCooldown = 0;
    this.rangedCooldownTime = 1.5; // seconds between shots

    // AoE enemy properties (M16)
    this.isAoe = config.isAoe || false;
    this.aoeRadius = config.aoeRadius || 32;
    this.aoeCooldown = 0;
    this.aoeCooldownTime = 2.0;

    // Callback for spawning projectiles (set by GameplayScene)
    this.onFireProjectile = null;

    // Callback for AoE damage (set by GameplayScene)
    this.onAoeAttack = null;

    this.state = 'idle'; // idle, chase, attack, hurt, dead
    this.isAlive = true;
    this.hurtTimer = 0;
    this.attackCooldown = 0;
    this.deathTimer = 0;

    // Stun state (M12 - ability system)
    this.stunTimer = 0;
    this.stunGfx = null;

    // Status effects (M18)
    this.statusEffects = new Map();

    // Knockback velocity
    this.knockbackX = 0;
    this.knockbackY = 0;

    // Pathfinding state
    this.path = [];        // Current A* path (array of {x, y} tile positions)
    this.pathTimer = 0;    // Time until next path recalculation
    this.pathIndex = 0;    // Current waypoint index in path

    // Patrol state (idle behavior)
    this.patrolTarget = null;  // {x, y} tile position to walk to
    this.patrolPause = 0;      // Pause timer between patrol moves

    this.container = new Container();
    this.gfx = null;
    this.createGraphics();
    this._syncVisual();
  }

  /**
   * Draw the enemy as a colored rectangle.
   */
  createGraphics() {
    this.gfx = new Graphics();
    const offset = (TILE_SIZE - this.size) / 2;
    this.gfx.rect(offset, offset, this.size, this.size);
    this.gfx.fill(this.color);
    this.container.addChild(this.gfx);

    // Stun indicator (M12) -- flashing yellow dot above enemy
    this.stunGfx = new Graphics();
    this.stunGfx.circle(TILE_SIZE / 2, -2, 2);
    this.stunGfx.fill(0xffff44);
    this.stunGfx.visible = false;
    this.container.addChild(this.stunGfx);
  }

  /**
   * Main update loop for enemy AI and state.
   * @param {number} dt - Delta time in seconds
   * @param {number} playerX - Player X position (top-left)
   * @param {number} playerY - Player Y position (top-left)
   * @param {number[][]} tiles - Dungeon tile grid
   * @param {import('../systems/Pathfinding.js').Pathfinding|null} pathfinding - Pathfinding instance
   */
  update(dt, playerX, playerY, tiles, pathfinding) {
    if (!this.isAlive) return;

    // Handle stun from status effect system (M18) -- stunned status sets stunTimer
    if (this.statusEffects && this.statusEffects.has('stunned')) {
      const stunnedState = this.statusEffects.get('stunned');
      if (stunnedState.timer > 0) {
        this.stunTimer = Math.max(this.stunTimer, stunnedState.timer);
      }
    }

    // Handle stun state (M12) -- skip AI when stunned
    if (this.stunTimer > 0) {
      this.stunTimer -= dt;
      // Show flashing yellow stun indicator
      if (this.stunGfx) {
        this.stunGfx.visible = true;
        this.stunGfx.alpha = (Math.sin(Date.now() * 0.01) + 1) * 0.5;
      }
      if (this.stunTimer <= 0) {
        this.stunTimer = 0;
        if (this.stunGfx) this.stunGfx.visible = false;
        // Return to idle after stun
        if (this.state !== 'dead' && this.state !== 'hurt') {
          this.state = 'idle';
        }
      }
      this._syncVisual();
      return;
    } else if (this.stunGfx) {
      this.stunGfx.visible = false;
    }

    // Tick attack cooldown
    if (this.attackCooldown > 0) {
      this.attackCooldown -= dt;
    }

    // Handle hurt state (knockback/flash)
    if (this.state === 'hurt') {
      this.hurtTimer -= dt;

      // Apply knockback
      if (this.knockbackX !== 0 || this.knockbackY !== 0) {
        const kbX = this.knockbackX * dt * 4; // decay over ~0.25s
        const kbY = this.knockbackY * dt * 4;
        this._applyMovement(kbX, kbY, tiles);
        this.knockbackX *= (1 - dt * 8);
        this.knockbackY *= (1 - dt * 8);
      }

      if (this.hurtTimer <= 0) {
        this.hurtTimer = 0;
        this.gfx.tint = 0xffffff;
        this.state = 'idle';
      }
      this._syncVisual();
      return;
    }

    // Handle dead state
    if (this.state === 'dead') {
      this.deathTimer -= dt;
      this.gfx.alpha = Math.max(0, this.deathTimer / 0.3);
      this._syncVisual();
      if (this.deathTimer <= 0) {
        this.isAlive = false;
      }
      return;
    }

    // Calculate distance to player (center to center)
    const enemyCenterX = this.x + TILE_SIZE / 2;
    const enemyCenterY = this.y + TILE_SIZE / 2;
    const playerCenterX = playerX + TILE_SIZE / 2;
    const playerCenterY = playerY + TILE_SIZE / 2;
    const dx = playerCenterX - enemyCenterX;
    const dy = playerCenterY - enemyCenterY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    const detectionRange = 6 * TILE_SIZE; // 96px
    const meleeAttackRange = 1.5 * TILE_SIZE;  // 24px
    const attackRange = this.isRanged ? this.rangedAttackRange : meleeAttackRange;

    // Tick ranged cooldown
    if (this.rangedCooldown > 0) {
      this.rangedCooldown -= dt;
    }

    // Tick AoE cooldown
    if (this.aoeCooldown > 0) {
      this.aoeCooldown -= dt;
    }

    // State transitions
    if (this.state === 'idle') {
      // Check if player is within detection range AND we have line of sight
      if (dist <= detectionRange && hasLineOfSight(enemyCenterX, enemyCenterY, playerCenterX, playerCenterY, tiles, TILE_SIZE)) {
        this.state = 'chase';
        this.path = [];
        this.pathTimer = 0; // Recalculate path immediately
        this.pathIndex = 0;
        this.patrolTarget = null;
      }
    }

    if (this.state === 'chase') {
      if (dist <= attackRange) {
        this.state = 'attack';
      } else if (dist > detectionRange * 1.5) {
        // Lost the player
        this.state = 'idle';
        this.path = [];
        this.pathIndex = 0;
      }
    }

    if (this.state === 'attack') {
      if (dist > attackRange * 1.5) {
        this.state = 'chase';
        this.pathTimer = 0; // Recalculate path when re-entering chase
      }
    }

    // Execute state behavior
    if (this.state === 'idle') {
      this._updatePatrol(dt, tiles);
    } else if (this.state === 'chase') {
      // Ranged enemies try to maintain distance instead of closing in
      if (this.isRanged && dist <= attackRange && dist > meleeAttackRange) {
        // In range, try to fire instead of chasing closer
        this.state = 'attack';
      } else {
        this._updateChase(dt, playerX, playerY, tiles, pathfinding);
      }
    }

    // Ranged attack: fire projectile toward player
    if (this.state === 'attack' && this.isRanged && this.rangedCooldown <= 0) {
      if (dist > 0 && this.onFireProjectile) {
        const ndx = dx / dist;
        const ndy = dy / dist;
        this.onFireProjectile(enemyCenterX, enemyCenterY, ndx, ndy, this.projectileSpeed, this.damage);
        this.rangedCooldown = this.rangedCooldownTime;
      }
    }

    // AoE attack: damage in radius around self
    if (this.state === 'attack' && this.isAoe && this.aoeCooldown <= 0 && dist <= meleeAttackRange * 1.5) {
      if (this.onAoeAttack) {
        this.onAoeAttack(enemyCenterX, enemyCenterY, this.aoeRadius, this.damage);
        this.aoeCooldown = this.aoeCooldownTime;
      }
    }

    // Melee attack state: enemy stands still, handled by CombatSystem

    this._syncVisual();
  }

  /**
   * Idle patrol behavior: pick a random adjacent walkable tile, walk there, pause, repeat.
   */
  _updatePatrol(dt, tiles) {
    // If pausing, count down
    if (this.patrolPause > 0) {
      this.patrolPause -= dt;
      return;
    }

    // If no patrol target, pick one
    if (!this.patrolTarget) {
      this._pickPatrolTarget(tiles);
      if (!this.patrolTarget) {
        // No walkable neighbor found, just pause
        this.patrolPause = 1.0 + Math.random();
        return;
      }
    }

    // Move toward patrol target (pixel center of the target tile)
    const targetPixelX = this.patrolTarget.x * TILE_SIZE;
    const targetPixelY = this.patrolTarget.y * TILE_SIZE;
    const pdx = targetPixelX - this.x;
    const pdy = targetPixelY - this.y;
    const pdist = Math.sqrt(pdx * pdx + pdy * pdy);

    if (pdist < 2) {
      // Arrived at patrol target
      this.x = targetPixelX;
      this.y = targetPixelY;
      this.patrolTarget = null;
      this.patrolPause = 1.0 + Math.random(); // Pause 1-2 seconds
      return;
    }

    // Move at half speed while patrolling
    const patrolSpeed = this.speed * 0.5;
    const nx = pdx / pdist;
    const ny = pdy / pdist;
    const moveX = nx * patrolSpeed * dt;
    const moveY = ny * patrolSpeed * dt;
    this._applyMovement(moveX, moveY, tiles);
  }

  /**
   * Pick a random walkable adjacent tile for patrol.
   */
  _pickPatrolTarget(tiles) {
    const currentCol = Math.floor((this.x + TILE_SIZE / 2) / TILE_SIZE);
    const currentRow = Math.floor((this.y + TILE_SIZE / 2) / TILE_SIZE);
    const height = tiles.length;
    const width = tiles[0].length;

    const candidates = [];
    const dirs = [
      { dx: 0, dy: -1 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 },
      { dx: 1, dy: 0 },
    ];

    for (const dir of dirs) {
      const nx = currentCol + dir.dx;
      const ny = currentRow + dir.dy;
      if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
        if (WALKABLE_TILES.has(tiles[ny][nx])) {
          candidates.push({ x: nx, y: ny });
        }
      }
    }

    if (candidates.length > 0) {
      this.patrolTarget = candidates[Math.floor(Math.random() * candidates.length)];
    } else {
      this.patrolTarget = null;
    }
  }

  /**
   * Chase state: use A* pathfinding to navigate toward the player.
   */
  _updateChase(dt, playerX, playerY, tiles, pathfinding) {
    const enemyCenterX = this.x + TILE_SIZE / 2;
    const enemyCenterY = this.y + TILE_SIZE / 2;
    const playerCenterX = playerX + TILE_SIZE / 2;
    const playerCenterY = playerY + TILE_SIZE / 2;

    // Recalculate path every 0.5 seconds
    this.pathTimer -= dt;
    if (this.pathTimer <= 0) {
      this.pathTimer = 0.5;
      this._recalculatePath(playerX, playerY, tiles, pathfinding);
    }

    // Follow path
    if (this.path && this.path.length > 0 && this.pathIndex < this.path.length) {
      const waypoint = this.path[this.pathIndex];
      // Target the center of the waypoint tile (aligned to top-left of entity)
      const targetX = waypoint.x * TILE_SIZE;
      const targetY = waypoint.y * TILE_SIZE;
      const wdx = targetX - this.x;
      const wdy = targetY - this.y;
      const wdist = Math.sqrt(wdx * wdx + wdy * wdy);

      if (wdist < 2) {
        // Close enough to waypoint, advance to next
        this.x = targetX;
        this.y = targetY;
        this.pathIndex++;
      } else {
        // Move toward waypoint (apply frozen speed modifier M18)
        const chaseSpeed = this._getEffectiveSpeed();
        const nx = wdx / wdist;
        const ny = wdy / wdist;
        const moveX = nx * chaseSpeed * dt;
        const moveY = ny * chaseSpeed * dt;
        this._applyMovement(moveX, moveY, tiles);
      }
    } else {
      // No valid path -- fall back to direct movement toward player
      const dx = playerCenterX - enemyCenterX;
      const dy = playerCenterY - enemyCenterY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist > 0) {
        const fallbackSpeed = this._getEffectiveSpeed();
        const nx = dx / dist;
        const ny = dy / dist;
        const moveX = nx * fallbackSpeed * dt;
        const moveY = ny * fallbackSpeed * dt;
        this._applyMovement(moveX, moveY, tiles);
      }
    }
  }

  /**
   * Recalculate the A* path to the player.
   */
  _recalculatePath(playerX, playerY, tiles, pathfinding) {
    if (!pathfinding) {
      this.path = [];
      this.pathIndex = 0;
      return;
    }

    const enemyTileX = Math.floor((this.x + TILE_SIZE / 2) / TILE_SIZE);
    const enemyTileY = Math.floor((this.y + TILE_SIZE / 2) / TILE_SIZE);
    const playerTileX = Math.floor((playerX + TILE_SIZE / 2) / TILE_SIZE);
    const playerTileY = Math.floor((playerY + TILE_SIZE / 2) / TILE_SIZE);

    const result = pathfinding.findPath(enemyTileX, enemyTileY, playerTileX, playerTileY);
    if (result && result.length > 0) {
      this.path = result;
      this.pathIndex = 0;
    } else {
      // No path found, clear it so fallback direct movement is used
      this.path = [];
      this.pathIndex = 0;
    }
  }

  /**
   * Apply damage to this enemy and trigger hurt state with knockback.
   * @param {number} amount - Damage amount
   * @param {number} knockbackX - Knockback direction X
   * @param {number} knockbackY - Knockback direction Y
   */
  takeDamage(amount, knockbackX, knockbackY) {
    if (!this.isAlive || this.state === 'dead') return;

    this.hp -= amount;
    this.knockbackX = knockbackX * 8;
    this.knockbackY = knockbackY * 8;

    if (this.hp <= 0) {
      this.hp = 0;
      this.state = 'dead';
      this.deathTimer = 0.3;
      this.gfx.tint = 0xff0000;
    } else {
      this.state = 'hurt';
      this.hurtTimer = 0.15;
      this.gfx.tint = 0xff0000;
    }
  }

  /**
   * Get effective speed accounting for status effects (M18).
   * @returns {number} Speed after applying frozen/stunned modifiers
   */
  _getEffectiveSpeed() {
    let speed = this.speed;
    if (this.statusEffects && this.statusEffects.size > 0) {
      if (this.statusEffects.has('frozen')) {
        speed *= 0.4;
      }
      if (this.statusEffects.has('stunned')) {
        speed = 0;
      }
    }
    return speed;
  }

  /**
   * Apply movement with tile collision (same approach as Player).
   */
  _applyMovement(moveX, moveY, tiles) {
    if (moveX !== 0) {
      const newX = this.x + moveX;
      if (this._canMoveTo(newX, this.y, tiles)) {
        this.x = newX;
      }
    }
    if (moveY !== 0) {
      const newY = this.y + moveY;
      if (this._canMoveTo(this.x, newY, tiles)) {
        this.y = newY;
      }
    }
  }

  _canMoveTo(x, y, tiles) {
    const pad = 1;
    const offset = (TILE_SIZE - this.size) / 2;
    const left = x + offset + pad;
    const right = x + offset + this.size - pad - 1;
    const top = y + offset + pad;
    const bottom = y + offset + this.size - pad - 1;

    return (
      this._isWalkable(left, top, tiles) &&
      this._isWalkable(right, top, tiles) &&
      this._isWalkable(left, bottom, tiles) &&
      this._isWalkable(right, bottom, tiles)
    );
  }

  _isWalkable(pixelX, pixelY, tiles) {
    const col = Math.floor(pixelX / TILE_SIZE);
    const row = Math.floor(pixelY / TILE_SIZE);
    const height = tiles.length;
    const width = tiles[0].length;

    if (row < 0 || row >= height || col < 0 || col >= width) {
      return false;
    }

    return WALKABLE_TILES.has(tiles[row][col]);
  }

  _syncVisual() {
    this.container.x = Math.round(this.x);
    this.container.y = Math.round(this.y);
  }
}
